import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import _ from 'lodash';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { Button } from '../../../../../common/button';
import useNotification from '../../../../../../core/hooks/use_notification';
import { getGenerateWarehouseDocumentRequestsNotificationData } from '../../../../../../core/utils/notification_utils';
import { getGenerateWarehouseDocumentRequestsAction, getGenerateWarehouseDocumentRequestsState, invalidateGenerateWarehouseDocumentRequests } from '../../../../../../features/inventory/get_generate_warehouse_document_requests';
import WarehouseDocumentHistory from './warehouse_document_history';
import WarehouseDocumentHistoryDetails from './warehouse_document_history_details';
import PaginationControls from '../../../../../common/pagination_controls';
import { getDefaultGenerateWarehouseDocumentRequestsPaginationQueryValues } from '../../../../../../core/utils/api_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../../core/constants';

const initialQueryParams = {
    ...getDefaultGenerateWarehouseDocumentRequestsPaginationQueryValues,
    fromDate: '',
    toDate: ''
};

const CopyHistoricalDialog = ({ form, onCopyHistoricalDocument }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getWarehouseDocumentHistory = useSelector(getGenerateWarehouseDocumentRequestsState);

    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState(0);
    const [documentIndex, setDocumentIndex] = useState(0);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    useEffect(() => {
        if (open) {
            const fromDate = queryParams?.fromDate && queryParams?.fromDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);
            const toDate = queryParams?.toDate && queryParams?.toDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);

            dispatch(getGenerateWarehouseDocumentRequestsAction({
                ..._.omit(form, ['deliveryDate']), ...queryParams,
                fromDate,
                toDate
            }))
                .then(response => {
                    showErrorMessage(getGenerateWarehouseDocumentRequestsNotificationData(response));
                });
        }
    }, [dispatch, open, form, queryParams, showErrorMessage]);

    const onOpen = () => {
        setQueryParams(initialQueryParams);
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
        setTab(0);
        dispatch(invalidateGenerateWarehouseDocumentRequests());
    };

    const onCancel = () => {
        setDocumentIndex(0);
        setTab(0);
    };

    const onChangeDateFrom = (fromDate) => {
        setTab(0);
        setQueryParams(prevState => ({ ...prevState, fromDate }));
    };

    const onChangeDateTo = (toDate) => {
        setTab(0);
        setQueryParams(prevState => ({ ...prevState, toDate }));
    };

    const onClickDocument = (index) => {
        setDocumentIndex(index);
        setTab(1);
    };

    const handleCopy = () => {
        onCopyHistoricalDocument(getWarehouseDocumentHistory?.data?.content?.[documentIndex]?.positions);
        onClose();
    };

    const renderView = () => {
        if (getWarehouseDocumentHistory?.loading) {
            return (
                <Box mt={ 6 } width="100%">
                    <LinearProgress/>
                </Box>
            );
        }

        if (getWarehouseDocumentHistory?.data) {
            if (tab === 0) {
                return (
                    getWarehouseDocumentHistory?.data?.content &&
                    <Box sx={ { width: '100%', mt: 3 } }>
                        <WarehouseDocumentHistory onClickDocument={ onClickDocument }/>
                        <Box mt={ 2 }>
                            <PaginationControls
                                currentPage={ getWarehouseDocumentHistory?.data?.pageable?.pageNumber }
                                totalPages={ getWarehouseDocumentHistory?.data?.totalPages }
                                setPage={ page => setQueryParams(prevState => ({ ...prevState, page })) }
                            />
                        </Box>
                    </Box>
                );
            } else {
                return (
                    <Box sx={ { width: '100%', mt: 3 } }>
                        <WarehouseDocumentHistoryDetails
                            documentIndex={ documentIndex }
                            type={ form?.type }/>
                    </Box>
                );
            }
        }

        return <></>;
    };

    return (
        <>
            <Button onClick={ onOpen } cursor="pointer">
                Wczytaj dane z historycznych dokumentów
            </Button>

            <Dialog
                open={ open }
                onClose={ onClose }
                fullWidth
                maxWidth="md"
                PaperProps={ { sx: { minHeight: 600, maxHeight: 800, borderRadius: 2 } } }
            >
                <DialogTitle sx={ { pb: 1 } }>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">
                                { tab === 0 ? "Historia dokumentów magazynowych" : "Szczegóły historycznego dokumentu" }
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                { tab === 0 ? "Wybierz dokument z listy, aby podejrzeć jego szczegóły." : "Zweryfikuj pozycje przed załadowaniem ich do formularza." }
                            </Typography>
                        </Box>

                        { tab === 0 && (
                            <Stack direction="row" spacing={ 2 }>
                                <DatePicker
                                    displayWeekNumber
                                    label="Data dostawy od"
                                    onChange={ newDate => newDate?.isValid() && onChangeDateFrom(newDate) }
                                    slotProps={ { textField: { size: 'small', InputLabelProps: { shrink: true } } } }
                                    sx={ { width: 180 } }
                                />
                                <DatePicker
                                    displayWeekNumber
                                    label="Data dostawy do"
                                    onChange={ newDate => newDate?.isValid() && onChangeDateTo(newDate) }
                                    slotProps={ { textField: { size: 'small', InputLabelProps: { shrink: true } } } }
                                    sx={ { width: 180 } }
                                />
                            </Stack>
                        ) }
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    { renderView() }
                </DialogContent>

                <DialogActions sx={ { px: 3, py: 2 } }>
                    <Stack direction="row" spacing={ 2 } width="100%" justifyContent="space-between">
                        <Button onClick={ onClose } variant="outlined">Zamknij</Button>

                        { tab === 1 && (
                            <Stack direction="row" spacing={ 2 }>
                                <Button onClick={ onCancel } variant="outlined">Wróć do listy</Button>
                                <Button onClick={ handleCopy }>Załaduj pozycje</Button>
                            </Stack>
                        ) }
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CopyHistoricalDialog;